'use client'

import { NavigationQueryResult } from '@repo/sanity/types'
import { useProducts } from '@repo/shop/hooks'
import { Close } from '@repo/ui/components/close'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@repo/ui/components/sheet'
import { Skeleton } from '@repo/ui/components/skeleton'
import { FunctionComponent, useCallback } from 'react'
import { useRecentlyViewed } from '../../../providers/RecentlyViewedProvider'
import { SHEET, useSheet } from '../../../providers/SheetProvider'
import { useWishlist } from '../../../providers/WishlistProvider'
import { PageDto } from '../../../types/page.types'
import CartProducts from '../../cart/CartProducts'
import WishlistTriggerButton from './WishlistTriggerButton'

interface Props {
  pageDto: PageDto
  navigation: NavigationQueryResult
}

const Description: FunctionComponent<{ dto: PageDto; qty: number }> = ({ dto, qty }) => {
  if (qty > 0) {
    const copy =
      qty === 1
        ? dto.dictionary?.wishlist?.yourwishlistcontainsxitem || 'Your wishlist contains %1 item.'
        : dto.dictionary?.wishlist?.yourwishlistcontainsxitems || 'Your wishlist contains %1 items.'

    return <>{copy.replace('%1', qty.toString())}</>
  }

  return <span className='inline-block min-h-[2rem]'>{dto.dictionary?.wishlist?.yourwishlistisempty || 'Your wishlist is empty.'}</span>
}

const WishlistSheet: FunctionComponent<Props> = ({ pageDto, navigation }) => {
  const { isSheetOpen, closeSheet } = useSheet()
  const open = isSheetOpen(SHEET.WISHLIST)
  const { wishlist, removeFromWishlist } = useWishlist()
  const { recentlyViewedProductUrlSlugs, removeRecentlyViewed } = useRecentlyViewed()
  const qty = wishlist.length

  const { data: wishlistProducts, isLoading: wishlistIsLoading } = useProducts({
    locale: pageDto.params.locale,
    options: { revalidateOnFocus: false },
    // Conditionally fetch.
    urlSlugs: open ? wishlist : [],
  })

  const { data: recentlyViewedProducts } = useProducts({
    locale: pageDto.params.locale,
    options: { revalidateOnFocus: false },
    // Conditionally fetch.
    urlSlugs: qty === 0 && open ? recentlyViewedProductUrlSlugs : [],
  })

  const handleRemoval = useCallback(
    (urlSlug: string) => {
      // Close sheet when no items left.
      if (qty <= 1) {
        closeSheet()
      }

      removeFromWishlist(urlSlug)
    },
    [qty, closeSheet, removeFromWishlist],
  )

  return (
    <>
      <WishlistTriggerButton pageDto={pageDto} navigation={navigation} />
      <Sheet open={open} onOpenChange={(open) => !open && closeSheet()}>
        <SheetContent side='right'>
          <SheetHeader>
            <SheetTitle>{pageDto.dictionary?.wishlist?.wishlist || 'Wishlist'}</SheetTitle>
          </SheetHeader>
          <SheetDescription>
            <Description dto={pageDto} qty={qty} />
          </SheetDescription>
          {qty > 0 && wishlistIsLoading && <Skeleton className='ml-3 h-[20.25rem] w-10/12' />}
          <CartProducts pageDto={pageDto} products={wishlistProducts || []} onClick={closeSheet} onRemove={(urlSlug) => handleRemoval(urlSlug)} />
          {qty === 0 && recentlyViewedProducts && (recentlyViewedProducts || [])?.length > 0 && (
            <div className='border-border border-t border-solid'>
              <h3 className='flex items-center gap-1 px-3 pb-1 pt-3 uppercase'>
                {pageDto.dictionary?.navigation?.recentlyviewed || 'Recently Viewed'}
                <button
                  className='hover:text-foreground text-neutral-500'
                  type='button'
                  onClick={removeRecentlyViewed}
                  aria-label={pageDto.dictionary?.navigation?.clear || 'Clear'}
                >
                  <Close ariaLabel={undefined} />
                </button>
              </h3>
              <CartProducts pageDto={pageDto} products={recentlyViewedProducts} onClick={closeSheet} />
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  )
}

export default WishlistSheet
